import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import Badge from '../../../components/ui/Badge';
import {
  ClockIcon,
  DocumentTextIcon,
  EyeIcon,
  FolderIcon,
  GeneralList,
  GeneralListSortableTh,
  PencilIcon,
  PlusIcon,
  Select,
  TrashIcon,
  useConfirm,
  useGeneralListPageSize,
  useGeneralListSort,
} from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { fetcher } from '../../../services/fetcher';
import { deletePmsWorkOrder } from '../../../services/mutations';
import { normalizePaginatedListResponse } from '../../../services/paginatedList';
import type { PmsWorkOrder } from './useData';

type BoardTab = 'open' | 'closed' | 'all';
type LookupRow = { id: number; code?: string | null; name?: string | null };

const CLOSED_STATUS_CODES = new Set(['COMPLETED', 'VERIFIED', 'CANCELLED']);

const statusVariant: Record<string, 'default' | 'info' | 'success' | 'danger' | 'warning'> = {
  DRAFT: 'default',
  PLANNED: 'info',
  DUE: 'warning',
  OVERDUE: 'danger',
  IN_PROGRESS: 'warning',
  PAUSED: 'info',
  AWAITING_PARTS: 'info',
  AWAITING_APPROVAL: 'warning',
  COMPLETED: 'success',
  VERIFIED: 'success',
  POSTPONED: 'default',
  CANCELLED: 'default',
};

const viewTabs: Array<{
  id: BoardTab;
  label: string;
  icon: React.ReactNode;
  emptyTitle: string;
  emptyHint: string;
}> = [
  {
    id: 'open',
    label: 'Open',
    icon: <ClockIcon className="h-4 w-4" />,
    emptyTitle: 'No open work orders',
    emptyHint: 'Open work orders waiting for execution appear here.',
  },
  {
    id: 'closed',
    label: 'Closed',
    icon: <FolderIcon className="h-4 w-4" />,
    emptyTitle: 'No closed work orders',
    emptyHint: 'Completed, verified, and cancelled work orders appear here.',
  },
  {
    id: 'all',
    label: 'All',
    icon: <DocumentTextIcon className="h-4 w-4" />,
    emptyTitle: 'No work orders yet',
    emptyHint: 'Create a work order from PMS due jobs or manually.',
  },
];

function listFrom<T>(rows: T[] | { data?: T[] } | undefined): T[] {
  return Array.isArray(rows) ? rows : rows?.data ?? [];
}

function vesselLabel(row: PmsWorkOrder) {
  const v = row.vessel as { vesselName?: string; name?: string } | null | undefined;
  return v?.vesselName ?? v?.name ?? (row.vesselId != null ? `#${row.vesselId}` : '');
}

function equipmentLabel(row: PmsWorkOrder) {
  const eq = row.equipment as { equipmentCode?: string; name?: string; code?: string } | null | undefined;
  if (!eq) return row.snapshotEquipmentName != null ? String(row.snapshotEquipmentName) : '';
  const code = eq.equipmentCode ?? eq.code;
  if (code && eq.name) return `${code} · ${eq.name}`;
  return eq.name ?? code ?? '';
}

function jobLabel(row: PmsWorkOrder) {
  const job = row.maintenanceJob as { jobCode?: string; title?: string } | null | undefined;
  if (job?.jobCode && job?.title) return `${job.jobCode} · ${job.title}`;
  if (job?.title) return job.title;
  if (row.snapshotJobCode && row.snapshotJobTitle) {
    return `${row.snapshotJobCode} · ${row.snapshotJobTitle}`;
  }
  return row.snapshotJobTitle != null ? String(row.snapshotJobTitle) : '';
}

function statusCode(row: PmsWorkOrder) {
  return String((row.status as { code?: string } | null)?.code ?? '').toUpperCase();
}

function statusLabel(row: PmsWorkOrder) {
  const s = row.status as { name?: string; code?: string } | null | undefined;
  return s?.name ?? s?.code ?? '';
}

function sourceLabel(row: PmsWorkOrder) {
  const s = row.sourceType as { name?: string; code?: string } | null | undefined;
  return s?.name ?? s?.code ?? '';
}

function formatDate(value?: unknown) {
  if (value == null || value === '') return '';
  const raw = String(value);
  const d = new Date(raw.length === 10 ? `${raw}T00:00:00.000Z` : raw);
  if (Number.isNaN(d.getTime())) return raw.slice(0, 10);
  return d.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: raw.length === 10 ? 'UTC' : undefined,
  });
}

function isClosed(row: PmsWorkOrder) {
  return CLOSED_STATUS_CODES.has(statusCode(row));
}

function sortValue(row: PmsWorkOrder, key: string): string | number {
  switch (key) {
    case 'workOrderNumber':
      return String(row.workOrderNumber ?? '').toLowerCase();
    case 'title':
      return String(row.title ?? '').toLowerCase();
    case 'vessel':
      return vesselLabel(row).toLowerCase();
    case 'equipment':
      return equipmentLabel(row).toLowerCase();
    case 'job':
      return jobLabel(row).toLowerCase();
    case 'status':
      return statusLabel(row).toLowerCase();
    case 'source':
      return sourceLabel(row).toLowerCase();
    case 'priority':
      return row.priority != null ? Number(row.priority) : Number.POSITIVE_INFINITY;
    case 'plannedDueDate':
      return row.plannedDueDate != null ? String(row.plannedDueDate).slice(0, 10) : '';
    case 'createdAt':
      return row.createdAt ? String(row.createdAt) : '';
    default:
      return '';
  }
}

export const WorkOrdersBoardPage: React.FC = () => {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const returnState = { from: `${location.pathname}${location.search}` };
  const confirm = useConfirm();

  const [tab, setTab] = useState<BoardTab>('open');
  const [page, setPage] = useState(1);
  const { itemsPerPage, setItemsPerPage, pageSizeOptions } = useGeneralListPageSize();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [vesselId, setVesselId] = useState('');
  const [statusId, setStatusId] = useState('');
  const [sourceTypeId, setSourceTypeId] = useState('');

  type VesselOptionRow = { id: number; vesselName: string; isActive?: boolean };
  const { data: vesselsRaw, isLoading: vesselsLoading } = useSWR<
    VesselOptionRow[] | { data?: VesselOptionRow[] }
  >('/vessels?page=1&limit=500', fetcher, { revalidateOnFocus: false });
  const { data: statusesRaw } = useSWR<LookupRow[] | { data?: LookupRow[] }>(
    '/pms-work-order-statuses?page=1&limit=500',
    fetcher,
    { revalidateOnFocus: false },
  );
  const { data: sourcesRaw } = useSWR<LookupRow[] | { data?: LookupRow[] }>(
    '/pms-work-order-sources?page=1&limit=500',
    fetcher,
    { revalidateOnFocus: false },
  );

  const vesselOptions = useMemo(() => {
    const list = listFrom(vesselsRaw).filter((v) => v.isActive !== false);
    return list.map((v) => ({ value: String(v.id), label: v.vesselName }));
  }, [vesselsRaw]);

  const statusOptions = useMemo(() => {
    const list = listFrom(statusesRaw);
    return list.map((s) => ({ value: String(s.id), label: s.name ?? s.code ?? `#${s.id}` }));
  }, [statusesRaw]);

  const sourceOptions = useMemo(() => {
    const list = listFrom(sourcesRaw);
    return list.map((s) => ({ value: String(s.id), label: s.name ?? s.code ?? `#${s.id}` }));
  }, [sourcesRaw]);

  const listUrl = useMemo(() => {
    const params = new URLSearchParams({ page: '1', limit: '500' });
    if (search.trim()) params.set('searchParams', search.trim());
    if (vesselId) params.set('vesselId', vesselId);
    if (statusId) params.set('statusId', statusId);
    if (sourceTypeId) params.set('sourceTypeId', sourceTypeId);
    return `/pms-work-orders?${params.toString()}`;
  }, [search, vesselId, statusId, sourceTypeId]);

  const { data, error, isLoading, mutate } = useSWR(listUrl, fetcher, {
    revalidateOnFocus: true,
  });

  useEffect(() => {
    const t = window.setTimeout(() => setSearch(searchInput), 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const { sortConfig, handleSort } = useGeneralListSort(
    { key: 'plannedDueDate', direction: 'asc' },
    {
      defaultDirectionForKey: (key) =>
        key === 'workOrderNumber' ||
        key === 'title' ||
        key === 'vessel' ||
        key === 'equipment' ||
        key === 'job' ||
        key === 'status' ||
        key === 'source'
          ? 'asc'
          : key === 'plannedDueDate' || key === 'priority'
            ? 'asc'
            : 'desc',
    },
  );

  useEffect(() => {
    setPage(1);
  }, [search, vesselId, statusId, sourceTypeId, tab, itemsPerPage]);

  useEffect(() => {
    setPage(1);
  }, [sortConfig.key, sortConfig.direction]);

  const baseRows = useMemo(() => {
    const normalized = normalizePaginatedListResponse<PmsWorkOrder>(data, 1, 500);
    return normalized.data;
  }, [data]);

  const tabRows = useMemo(() => {
    if (tab === 'all') return baseRows;
    return baseRows.filter((row) => (tab === 'closed' ? isClosed(row) : !isClosed(row)));
  }, [baseRows, tab]);

  const openCount = useMemo(() => baseRows.filter((row) => !isClosed(row)).length, [baseRows]);
  const closedCount = useMemo(() => baseRows.filter((row) => isClosed(row)).length, [baseRows]);

  const sortedRows = useMemo(() => {
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    return [...tabRows].sort((a, b) => {
      const av = sortValue(a, sortConfig.key);
      const bv = sortValue(b, sortConfig.key);
      if (typeof av === 'number' && typeof bv === 'number') {
        if (av === bv) return 0;
        return av < bv ? -dir : dir;
      }
      return String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' }) * dir;
    });
  }, [tabRows, sortConfig.key, sortConfig.direction]);

  const total = sortedRows.length;
  const totalPages = Math.max(1, Math.ceil(total / itemsPerPage) || 1);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const pageRows = useMemo(() => {
    const start = (page - 1) * itemsPerPage;
    return sortedRows.slice(start, start + itemsPerPage);
  }, [sortedRows, page, itemsPerPage]);

  const hasFilters = Boolean(search.trim() || vesselId || statusId || sourceTypeId);

  const clearFilters = () => {
    setSearchInput('');
    setSearch('');
    setVesselId('');
    setStatusId('');
    setSourceTypeId('');
  };

  const switchTab = (next: BoardTab) => {
    if (next === tab) return;
    setTab(next);
  };

  const activeTab = useMemo(() => viewTabs.find((t) => t.id === tab) ?? viewTabs[0], [tab]);

  const tabCounts: Record<BoardTab, number | undefined> = {
    open: openCount,
    closed: closedCount,
    all: baseRows.length,
  };

  const handleDelete = async (e: React.MouseEvent, row: PmsWorkOrder) => {
    e.stopPropagation();
    const label =
      row.workOrderNumber != null ? String(row.workOrderNumber) : `WO #${row.id}`;
    const ok = await confirm({
      title: 'Delete work order',
      message: `Delete “${label}”?`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      confirmVariant: 'danger',
    });
    if (!ok) return;
    try {
      await deletePmsWorkOrder(row.id);
      await mutate();
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : null;
      window.alert(msg || (err instanceof Error ? err.message : 'Failed to delete work order'));
    }
  };

  return (
    <GeneralList
      tabs={viewTabs.map(({ id, label, icon }) => ({
        id,
        label,
        icon,
        count: tabCounts[id],
      }))}
      activeTab={tab}
      onTabChange={(value) => switchTab(value as BoardTab)}
      sorting={{ sortConfig, onSort: handleSort }}
      search={{
        value: searchInput,
        onChange: setSearchInput,
        placeholder: 'Search work orders…',
        title: 'Searches WO number, title, job code, equipment code',
      }}
      actions={
        <Link
          to="/pms/pms-work-orders/new"
          state={returnState}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-xl px-3.5 py-2 text-sm font-medium text-white shadow-sm',
            colors.primary,
          )}
        >
          <PlusIcon className="h-4 w-4" />
          New work order
        </Link>
      }
      filters={
        <div className="flex flex-wrap items-center gap-2">
          <div className="w-[150px]">
            <Select
              value={vesselId}
              options={vesselOptions}
              placeholder={vesselsLoading ? 'Loading vessels…' : 'Vessel'}
              showClearButton
              searchPlaceholder="Search vessels…"
              onChange={(e) => setVesselId(String(e.target.value))}
            />
          </div>
          <div className="w-[140px]">
            <Select
              value={statusId}
              options={statusOptions}
              placeholder="Status"
              showClearButton
              onChange={(e) => setStatusId(String(e.target.value))}
            />
          </div>
          <div className="w-[150px]">
            <Select
              value={sourceTypeId}
              options={sourceOptions}
              placeholder="Source"
              showClearButton
              onChange={(e) => setSourceTypeId(String(e.target.value))}
            />
          </div>
        </div>
      }
      filtersInline
      hasFilters={hasFilters}
      onClearFilters={clearFilters}
      isLoading={isLoading}
      loadingLabel="Loading work orders…"
      isEmpty={!error && pageRows.length === 0}
      emptyState={{
        icon:
          tab === 'open' && !hasFilters ? (
            <DocumentTextIcon className="h-12 w-12" />
          ) : (
            <FolderIcon className="h-12 w-12" />
          ),
        title: hasFilters ? 'No matching work orders' : activeTab.emptyTitle,
        hint: hasFilters ? 'Try adjusting filters or clearing the search.' : activeTab.emptyHint,
        action: hasFilters ? (
          <button
            type="button"
            onClick={clearFilters}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-xl border px-4 py-2.5 text-sm font-medium',
              colors.border,
              colors.text,
            )}
          >
            Clear filters
          </button>
        ) : (
          <Link
            to="/pms/pms-work-orders/new"
            state={returnState}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-medium text-white',
              colors.primary,
            )}
          >
            <PlusIcon className="h-4 w-4" />
            New work order
          </Link>
        ),
      }}
      banner={
        error ? (
          <p className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-300">
            Could not load work orders.
          </p>
        ) : null
      }
      pagination={{
        page,
        totalPages,
        total,
        itemsPerPage,
        pageSizeOptions,
        onPageChange: setPage,
        onItemsPerPageChange: (size) => {
          setItemsPerPage(size);
          setPage(1);
        },
      }}
    >
      <table className="w-max border-separate border-spacing-0 text-sm">
        <thead>
          <tr className={cn('border-b text-left text-xs uppercase tracking-wide', colors.border, colors.textMuted)}>
            <th
              className={cn(
                'sticky left-0 z-20 whitespace-nowrap px-3 py-3 font-medium',
                colors.surface,
              )}
              style={{ minWidth: '5.5rem' }}
            />
            <GeneralListSortableTh sortKey="workOrderNumber" className="whitespace-nowrap" style={{ minWidth: '14rem' }}>
              Work order
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="vessel" className="whitespace-nowrap" style={{ minWidth: '9rem' }}>
              Vessel
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="equipment" className="whitespace-nowrap" style={{ minWidth: '12rem' }}>
              Equipment
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="plannedDueDate" className="whitespace-nowrap" style={{ minWidth: '7.5rem' }}>
              Due
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="status" className="whitespace-nowrap" style={{ minWidth: '8rem' }}>
              Status
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="source" className="whitespace-nowrap" style={{ minWidth: '8rem' }}>
              Source
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="priority" className="whitespace-nowrap" style={{ minWidth: '4rem' }} align="center">
              Pri
            </GeneralListSortableTh>
          </tr>
        </thead>
        <tbody>
          {pageRows.map((row) => {
            const code = statusCode(row);
            const woNumber =
              row.workOrderNumber != null ? String(row.workOrderNumber) : `WO #${row.id}`;
            return (
              <tr
                key={row.id}
                onClick={() => navigate(`/pms/pms-work-orders/${row.id}`, { state: returnState })}
                className={cn(
                  'group cursor-pointer border-b transition last:border-b-0 hover:bg-slate-50/80 dark:hover:bg-slate-800/40',
                  colors.border,
                )}
              >
                <td
                  className={cn(
                    'sticky left-0 z-10 whitespace-nowrap px-3 py-3.5',
                    colors.surface,
                    'group-hover:bg-slate-50 dark:group-hover:bg-slate-800',
                  )}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex flex-nowrap items-center">
                    <Link
                      to={`/pms/pms-work-orders/${row.id}`}
                      state={returnState}
                      title="View"
                      aria-label="View work order"
                      className="rounded-lg p-1 text-indigo-600 transition-colors hover:bg-indigo-50 hover:text-indigo-900"
                    >
                      <EyeIcon className="h-5 w-5" />
                    </Link>
                    <Link
                      to={`/pms/pms-work-orders/${row.id}/edit`}
                      state={returnState}
                      title="Edit"
                      aria-label="Edit work order"
                      className="rounded-lg p-1 text-blue-600 transition-colors hover:bg-blue-50 hover:text-blue-900"
                    >
                      <PencilIcon className="h-5 w-5" />
                    </Link>
                    <button
                      type="button"
                      title="Delete"
                      aria-label="Delete work order"
                      onClick={(e) => handleDelete(e, row)}
                      className="rounded-lg p-1 text-red-600 transition-colors hover:bg-red-50 hover:text-red-900"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </td>
                <td className="max-w-[18rem] px-3 py-3.5">
                  <div className="min-w-0">
                    <p className={cn('truncate font-medium', colors.text)}>{woNumber}</p>
                    <p className={cn('mt-0.5 truncate text-xs', colors.textMuted)}>
                      {row.title != null ? String(row.title) : ''}
                    </p>
                  </div>
                </td>
                <td
                  className={cn('max-w-[12rem] truncate px-3 py-3.5', colors.textMuted)}
                  title={vesselLabel(row)}
                >
                  {vesselLabel(row)}
                </td>
                <td
                  className={cn('max-w-[14rem] truncate px-3 py-3.5', colors.textMuted)}
                  title={equipmentLabel(row)}
                >
                  {equipmentLabel(row)}
                </td>
                <td className={cn('whitespace-nowrap px-3 py-3.5', colors.textMuted)}>
                  {formatDate(row.plannedDueDate)}
                </td>
                <td className="whitespace-nowrap px-3 py-3.5">
                  <Badge variant={statusVariant[code] ?? 'default'} className="whitespace-nowrap">
                    {statusLabel(row)}
                  </Badge>
                </td>
                <td
                  className={cn('max-w-[10rem] truncate px-3 py-3.5', colors.text)}
                  title={sourceLabel(row)}
                >
                  {sourceLabel(row)}
                </td>
                <td className={cn('text-center tabular-nums px-3 py-3.5', colors.textMuted)}>
                  {row.priority != null ? String(row.priority) : ''}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </GeneralList>
  );
};

export default WorkOrdersBoardPage;
